import { twJoin } from "tailwind-merge";

import { ToolCallGroup } from "src/base-components/agents/ToolCallGroup";
import { ChatLoadingIndicator } from "src/base-components/Chat/ChatLoadingIndicator";
import { ChatMessage } from "src/base-components/Chat/ChatMessage";
import { ChatUserBubble } from "src/base-components/Chat/ChatUserBubble";
import {
  type AIMessage,
  type FileContentBlock,
  type HumanMessage,
  type LiveStatusDetail,
  type SessionMessagesResponseMessagesInner,
  type ToolMessage,
  ToolType,
} from "src/clients/agent-management";
import { UserMessageAttachments } from "src/unifiedCopilot/chat/attachments/UserMessageAttachments";
import {
  aiTurnItems,
  buildToolResultMap,
  type ChatTurn,
  chatTurns,
  type TurnItem,
} from "src/unifiedCopilot/chat/chatTurns";
import {
  humanMessageContentBlocksToMarkdown,
  mapAttachmentsToCopilotFileBlocks,
  mapPromptContentToCopilotContentBlocks,
} from "src/unifiedCopilot/chat/contentBlocks";
import type { QueuedDraft } from "src/unifiedCopilot/chat/UnifiedCopilotChatSessionContext";
import { UnifiedCopilotMarkdownRenderer } from "src/unifiedCopilot/chat/UnifiedCopilotMarkdownRenderer";
import { TestRunCard } from "src/unifiedCopilot/runs/TestRunCard";
import { TestRunProgressCard } from "src/unifiedCopilot/runs/TestRunProgressCard";
import { TestRunResultsLink } from "src/unifiedCopilot/runs/TestRunResultsLink";
import {
  isSettledRun,
  parsePolledRun,
  parseTestRunArtifact,
  type TestRunArtifact,
} from "src/unifiedCopilot/runs/testRunRef";

/** AI text runs render through the copilot renderer so inline resource
 * mentions become pills. */
const CopilotAIText: React.FC<{ text: string }> = ({ text }) => (
  <div className="overflow-auto">
    <UnifiedCopilotMarkdownRenderer content={text} />
  </div>
);

const renderThreadText = (text: string): React.ReactNode => (
  <CopilotAIText text={text} />
);

const aiToolCallBlocks = (turn: ChatTurn) =>
  turn.messages
    .filter((m): m is { type: "ai" } & AIMessage => m.type === "ai")
    .flatMap((message) => message.content_blocks ?? []);

/**
 * Dataset test runs the turn's tool results describe, one entry per run.
 *
 * A run is usually named twice: by the call that started it and by the call
 * the runner waited inside. Both carry the run's id, so they are collapsed
 * onto the report that knows how the run ended.
 */
const turnTestRuns = (
  turn: ChatTurn,
  toolResults: Map<string, ToolMessage>,
): { key: string; artifact: TestRunArtifact }[] => [
  ...aiToolCallBlocks(turn)
    .reduce<Map<string, { key: string; artifact: TestRunArtifact }>>(
      (byRun, block) => {
        if (block.type !== "tool_call") {
          return byRun;
        }
        const artifact = parseTestRunArtifact(toolResults.get(block.id));
        if (artifact === null) {
          return byRun;
        }
        const reported = byRun.get(artifact.testRunId);
        if (reported === undefined || !isSettledRun(reported.artifact)) {
          byRun.set(artifact.testRunId, { key: block.id, artifact });
        }
        return byRun;
      },
      new Map(),
    )
    .values(),
];

/**
 * Guards the live progress card against a live status that outlived the tool
 * call it described — without an open call there is nothing being waited on.
 *
 * The structured-output call is excluded: it carries the final answer and is
 * open for the moment the agent composes it, which would otherwise re-arm the
 * card after the run tool already returned.
 */
const hasUnansweredToolCall = (
  turn: ChatTurn,
  toolResults: Map<string, ToolMessage>,
): boolean =>
  aiToolCallBlocks(turn).some(
    (block) =>
      block.type === "tool_call" &&
      block.metadata.type !== ToolType.STRUCTURED_OUTPUT &&
      !toolResults.has(block.id),
  );

const TurnItemContent: React.FC<{ item: TurnItem; isActive: boolean }> = ({
  item,
  isActive,
}) => {
  if (item.kind === "text") {
    return <CopilotAIText text={item.text} />;
  }
  return (
    <ToolCallGroup
      alwaysShowToolDetails
      dataLoc="unified-copilot-tool-thread"
      items={item.items}
      renderText={renderThreadText}
      sessionSubstatus={isActive ? "running" : null}
      showDescription={false}
      showTokenUsage={false}
    />
  );
};

const UserMessageContent: React.FC<{
  contentBlocks: HumanMessage["content_blocks"];
}> = ({ contentBlocks }) => {
  const blocks = contentBlocks ?? [];
  const fileBlocks = blocks.filter(
    (block): block is { type: "file" } & FileContentBlock =>
      block.type === "file",
  );
  const markdown = humanMessageContentBlocksToMarkdown(blocks);
  return (
    <ChatMessage dataLoc="unified-copilot-message-user" role="user">
      {/* An attachment-only message has no bubble to show. */}
      {markdown.trim().length > 0 && (
        <ChatUserBubble>
          <UnifiedCopilotMarkdownRenderer content={markdown} />
        </ChatUserBubble>
      )}
      {fileBlocks.length > 0 && (
        <UserMessageAttachments fileBlocks={fileBlocks} />
      )}
    </ChatMessage>
  );
};

export const UnifiedCopilotMessages: React.FC<{
  messages: SessionMessagesResponseMessagesInner[];
  isActive: boolean;
  /** Drafts held while a stopped run unwinds; rendered like sent messages. */
  queuedDrafts?: QueuedDraft[];
  liveStatusDetail?: LiveStatusDetail | null;
  lastTurnRef?: React.Ref<HTMLLIElement>;
  reservesTurnHeight?: boolean;
}> = ({
  messages,
  isActive,
  queuedDrafts = [],
  liveStatusDetail = null,
  lastTurnRef,
  reservesTurnHeight = false,
}) => {
  const toolResults = buildToolResultMap(messages);
  const turns = chatTurns(messages);
  const polledRun = parsePolledRun(liveStatusDetail);

  const status = isActive ? (
    <ChatLoadingIndicator mode="loading" dataLoc="unified-copilot-thinking" />
  ) : null;

  return (
    <>
      {turns.map((turn, turnIdx) => {
        const isLastTurn = turnIdx === turns.length - 1;
        // With drafts queued, the scroll anchor moves to the last queued li.
        const isAnchorTurn = isLastTurn && queuedDrafts.length === 0;
        const humanMessage = turn.messages.find(
          (m): m is { type: "human" } & HumanMessage => m.type === "human",
        );
        const aiItems = aiTurnItems(turn, toolResults, isLastTurn && isActive);
        const testRuns = turnTestRuns(turn, toolResults);
        // A run whose tool call already returned is reported by its result
        // card, which follows a handed-off run itself. The live status can
        // still name that run until its own `until` elapses, so without this
        // the same run would show twice.
        const reportedRunIds = new Set(
          testRuns.map(({ artifact }) => artifact.testRunId),
        );
        const liveRun =
          isLastTurn &&
          polledRun !== null &&
          !reportedRunIds.has(polledRun.testRunId) &&
          hasUnansweredToolCall(turn, toolResults)
            ? polledRun
            : null;
        return (
          <li
            key={turn.key}
            ref={isAnchorTurn ? lastTurnRef : undefined}
            className={twJoin(
              // scroll-mt-2 matches the list's gap-y-2, so the gap above a
              // pinned turn cannot show the previous answer.
              "flex shrink-0 scroll-mt-2 flex-col",
              isAnchorTurn && reservesTurnHeight && "min-h-full",
            )}
            data-loc="unified-copilot-turn"
          >
            <ul className="flex flex-col gap-y-2">
              {humanMessage && (
                <UserMessageContent
                  contentBlocks={humanMessage.content_blocks}
                />
              )}
              {(aiItems.length > 0 ||
                testRuns.length > 0 ||
                liveRun !== null) && (
                <ChatMessage
                  dataLoc="unified-copilot-message-ai"
                  role="assistant"
                >
                  <div className="flex flex-col gap-2">
                    {aiItems.map((item) => (
                      <TurnItemContent
                        key={item.key}
                        isActive={isActive}
                        item={item}
                      />
                    ))}
                    {testRuns.map(({ key, artifact }) =>
                      // Only a run that is over can be rendered from its
                      // artifact. One still going — handed off, or merely
                      // started by this call — keeps reading the run API.
                      !isSettledRun(artifact) ? (
                        <TestRunProgressCard
                          key={key}
                          action={
                            <TestRunResultsLink
                              flowVersionId={artifact.flowVersionId}
                              testRunId={artifact.testRunId}
                            />
                          }
                          dataLoc="unified-copilot-test-run-result"
                          flowVersionId={artifact.flowVersionId}
                          initialProgress={artifact.progress}
                          testRunId={artifact.testRunId}
                        />
                      ) : (
                        <TestRunCard
                          key={key}
                          action={
                            <TestRunResultsLink
                              flowVersionId={artifact.flowVersionId}
                              testRunId={artifact.testRunId}
                            />
                          }
                          dataLoc="unified-copilot-test-run-result"
                          progress={artifact.progress}
                          status={
                            artifact.runStatus === "failed"
                              ? "failed"
                              : "finished"
                          }
                        />
                      ),
                    )}
                    {liveRun !== null && (
                      <TestRunProgressCard
                        action={
                          <TestRunResultsLink
                            flowVersionId={liveRun.flowVersionId}
                            testRunId={liveRun.testRunId}
                          />
                        }
                        dataLoc="unified-copilot-test-run-progress"
                        flowVersionId={liveRun.flowVersionId}
                        initialProgress={liveRun.progress}
                        testRunId={liveRun.testRunId}
                      />
                    )}
                  </div>
                </ChatMessage>
              )}
              {isLastTurn && status}
            </ul>
          </li>
        );
      })}
      {queuedDrafts.map((draft, draftIdx) => {
        const isAnchorDraft = draftIdx === queuedDrafts.length - 1;
        return (
          <li
            key={draft.id}
            ref={isAnchorDraft ? lastTurnRef : undefined}
            className={twJoin(
              "flex shrink-0 scroll-mt-2 flex-col",
              isAnchorDraft && reservesTurnHeight && "min-h-full",
            )}
            data-loc="unified-copilot-queued-turn"
          >
            <ul className="flex flex-col gap-y-2">
              <UserMessageContent
                contentBlocks={
                  // Invoke-request blocks are structurally message blocks — the
                  // same equivalence the optimistic cache message relies on.
                  [
                    ...mapPromptContentToCopilotContentBlocks(draft.markdown),
                    ...mapAttachmentsToCopilotFileBlocks(draft.attachments),
                  ] as HumanMessage["content_blocks"]
                }
              />
            </ul>
          </li>
        );
      })}
      {turns.length === 0 && status}
    </>
  );
};
